import { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
    fetchMikroSettings,
    upsertMikroSettings,
    testMikroConnection,
    fetchMikroSyncQueue,
    retryMikroSyncItem,
} from "../../../api/requests/mikro-requests";

// Sırlar backend'den DÖNMEZ; yalnızca "girildi mi" bilgisi (apiKeySet/keyTokenSet/sifreSet) gelir.
const EMPTY_SECRETS = { apiKey: false, keyToken: false, sifre: false };

// Boş bırakılan sır alanı = "mevcut değeri koru" (backend blank gelenleri yok sayar)
const secretOrNull = (v) => (v && v.trim() ? v.trim() : null);
const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

export const useMikroSettings = (companyId, onSaved) => {
    const [loading, setLoading] = useState(false);
    const [hasSettings, setHasSettings] = useState(false);
    const [connected, setConnected] = useState(false);
    // Hangi sırlar sunucuda tanımlı? → "tanımlı — değiştirmek için yeniden girin"
    const [secretsSet, setSecretsSet] = useState(EMPTY_SECRETS);
    const [testing, setTesting] = useState(false);

    // Senkron kuyruğu state'leri
    const [queue, setQueue] = useState({ content: [], totalElements: 0, totalPages: 0, page: 0, size: 10 });
    const [queueLoading, setQueueLoading] = useState(false);
    const [queueStatus, setQueueStatus] = useState("");
    const [retryingId, setRetryingId] = useState(null);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            companyId: companyId || "",
            apiBaseUrl: "",
            apiKey: "",
            keyToken: "",
            kullaniciKodu: "",
            sifre: "",
            firmaKodu: "",
            firmaNo: 0,
            subeNo: 0,
            calismaYili: new Date().getFullYear(),
            defaultKasaKodu: "",
            defaultBankaKodu: "",
            enabled: true,
        },
        validationSchema: Yup.object({
            companyId: Yup.string().required("Gerekli"),
            firmaKodu: Yup.string().trim().required("Firma kodu gerekli"),
            kullaniciKodu: Yup.string().trim().required("Kullanıcı kodu gerekli"),
            firmaNo: Yup.number().typeError("Sayı olmalı").integer("Tam sayı olmalı").min(0, "0 veya üzeri"),
            subeNo: Yup.number().typeError("Sayı olmalı").integer("Tam sayı olmalı").min(0, "0 veya üzeri"),
            calismaYili: Yup.number()
                .typeError("Sayı olmalı")
                .integer("Tam sayı olmalı")
                .min(2000, "2000 veya sonrası")
                .max(2100, "2100 veya öncesi")
                .required("Çalışma yılı gerekli"),
            // Sır alanları: sunucuda tanımlıysa boş bırakılabilir (mevcut değer korunur)
            apiKey: Yup.string().test("apiKey-required", "API Key gerekli", function (value) {
                return !!(value && value.trim()) || secretsSet.apiKey;
            }),
            sifre: Yup.string().test("sifre-required", "Parola gerekli", function (value) {
                return !!(value && value.trim()) || secretsSet.sifre;
            }),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const payload = {
                    companyId: values.companyId,
                    enabled: values.enabled,
                    apiBaseUrl: values.apiBaseUrl?.trim() || null,
                    // Sırlar: boş bırakılırsa backend mevcut değeri korur
                    apiKey: secretOrNull(values.apiKey),
                    keyToken: secretOrNull(values.keyToken),
                    sifre: secretOrNull(values.sifre),
                    kullaniciKodu: values.kullaniciKodu?.trim() || null,
                    firmaKodu: values.firmaKodu?.trim() || null,
                    firmaNo: numOrNull(values.firmaNo),
                    subeNo: numOrNull(values.subeNo),
                    calismaYili: numOrNull(values.calismaYili),
                    defaultKasaKodu: values.defaultKasaKodu?.trim() || null,
                    defaultBankaKodu: values.defaultBankaKodu?.trim() || null,
                };

                const res = await upsertMikroSettings(payload);
                if (res?.data) {
                    setHasSettings(true);
                    setConnected(!!res.data.connected);
                    setSecretsSet({
                        apiKey: !!res.data.apiKeySet,
                        keyToken: !!res.data.keyTokenSet,
                        sifre: !!res.data.sifreSet,
                    });
                    // Kaydedilen sırları formda tutma
                    formik.setFieldValue("apiKey", "", false);
                    formik.setFieldValue("keyToken", "", false);
                    formik.setFieldValue("sifre", "", false);
                    toast.success("Mikro ayarları kaydedildi");
                    onSaved?.();
                } else {
                    toast.error(res?.message || "Mikro ayarları kaydedilemedi");
                }
            } catch (error) {
                toast.error("Mikro ayarları kaydedilirken hata oluştu");
            } finally {
                setLoading(false);
            }
        },
    });

    useEffect(() => {
        if (!companyId) return;

        const loadSettings = async () => {
            try {
                const res = await fetchMikroSettings(companyId);

                if (res?.data) {
                    setHasSettings(true);
                    setConnected(!!res.data.connected);
                    setSecretsSet({
                        apiKey: !!res.data.apiKeySet,
                        keyToken: !!res.data.keyTokenSet,
                        sifre: !!res.data.sifreSet,
                    });
                    // Güvenlik: apiKey/keyToken/sifre backend'den DÖNMEZ; boş bırakılır,
                    // değiştirmek için yeniden girilmesi gerekir.
                    formik.setValues({
                        companyId,
                        apiBaseUrl: res.data.apiBaseUrl ?? "",
                        apiKey: "",
                        keyToken: "",
                        kullaniciKodu: res.data.kullaniciKodu ?? "",
                        sifre: "",
                        firmaKodu: res.data.firmaKodu ?? "",
                        firmaNo: res.data.firmaNo ?? 0,
                        subeNo: res.data.subeNo ?? 0,
                        calismaYili: res.data.calismaYili ?? new Date().getFullYear(),
                        defaultKasaKodu: res.data.defaultKasaKodu ?? "",
                        defaultBankaKodu: res.data.defaultBankaKodu ?? "",
                        enabled: res.data.enabled ?? true,
                    });
                } else {
                    setHasSettings(false);
                    setSecretsSet(EMPTY_SECRETS);
                }
            } catch (error) {
                console.error("Mikro ayarları yüklenemedi", error);
                setHasSettings(false);
                setSecretsSet(EMPTY_SECRETS);
            }
        };

        loadSettings();
    }, [companyId]);

    const testConnection = async () => {
        setTesting(true);
        try {
            const res = await testMikroConnection(companyId);
            if (res?.data === true) {
                toast.success("Mikro bağlantısı başarılı");
            } else {
                toast.error(res?.message || "Mikro bağlantısı başarısız");
            }
        } catch (error) {
            toast.error("Bağlantı testi sırasında hata oluştu");
        } finally {
            setTesting(false);
        }
    };

    const loadQueue = useCallback(async (page = 0, status = queueStatus, size = queue.size) => {
        if (!companyId) return;
        setQueueLoading(true);
        try {
            const res = await fetchMikroSyncQueue(companyId, { status, page, size });
            if (res?.data) {
                setQueue({
                    content: Array.isArray(res.data.content) ? res.data.content : [],
                    totalElements: res.data.totalElements ?? 0,
                    totalPages: res.data.totalPages ?? 0,
                    page: res.data.page ?? page,
                    size: res.data.size ?? size,
                });
            }
        } catch (error) {
            console.error("Mikro kuyruğu yüklenemedi", error);
        } finally {
            setQueueLoading(false);
        }
    }, [companyId, queueStatus, queue.size]);

    const changeQueueStatus = (status) => {
        setQueueStatus(status);
        loadQueue(0, status);
    };

    const retryItem = async (queueId) => {
        setRetryingId(queueId);
        try {
            const res = await retryMikroSyncItem(companyId, queueId);
            if (res?.data === true) {
                toast.success("Kayıt yeniden denemeye alındı");
                loadQueue(queue.page);
            } else {
                toast.error(res?.message || "Yeniden deneme başarısız");
            }
        } catch (error) {
            toast.error("Yeniden deneme sırasında hata oluştu");
        } finally {
            setRetryingId(null);
        }
    };

    return {
        formik,
        loading,
        hasSettings,
        connected,
        secretsSet,
        testing,
        testConnection,
        queue,
        queueLoading,
        queueStatus,
        changeQueueStatus,
        loadQueue,
        retryingId,
        retryItem,
    };
};
